import React, { useEffect, useState } from 'react';
import Navbar from './Features/Navbar';
import hr from '../images/icons/hr.png';
import { Box, CircularProgress, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Footer from './Home/Footer';
import { Fade } from 'react-awesome-reveal';
import StrapiRichText from './StrapiRichText';
import { fetchStrapiSingleType } from '../services/strapiClient';
import { pickCmsText, pickCmsTitle } from '../utils/cmsContent';

const richTextSx = {
  fontSize: '20px',
  lineHeight: 1.8,
  fontFamily: 'Montserrat',
  color: '#2f3036',
  fontWeight: 500,
  '& p': { marginTop: 0, marginBottom: '1rem' },
  '& p:last-child': { marginBottom: 0 },
  '& ul, & ol': { marginBottom: '1rem', paddingLeft: '1.25rem' },
  '& ul ul': { marginTop: '0.5rem', marginBottom: '0.5rem' },
  '& li': { marginBottom: '0.35rem' },
  '& strong': { fontWeight: 700 },
  '& a': { color: '#1398aa' },
  '& u': { textDecoration: 'underline', fontStyle: 'italic' },
  '& h2': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '34px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& h3': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '28px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& table': { borderCollapse: 'collapse', width: '100%', marginBottom: '2rem' },
  '& th': {
    backgroundColor: '#EBEAFF',
    fontWeight: 600,
    fontFamily: 'Montserrat',
    border: '1px solid rgba(224, 224, 224, 1)',
    padding: '16px',
  },
  '& td': {
    fontFamily: 'Montserrat',
    border: '1px solid rgba(224, 224, 224, 1)',
    padding: '16px',
    verticalAlign: 'top',
  },
  '& .service-callout': {
    borderStyle: 'solid',
    borderRadius: '20px',
    backgroundColor: '#EBEAFF',
    borderColor: '#EBEAFF',
    marginBottom: '3rem',
    padding: '20px',
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '20px',
    color: '#2f3036',
    fontWeight: 600,
  },
  '& .service-callout p': { marginTop: 0, marginBottom: '1rem' },
  '& .service-callout p:last-child': { marginBottom: 0 },
};

function normalizeRich(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function hasCms(remote) {
  const hasStructuredTable = Array.isArray(remote?.tableColumns) && remote.tableColumns.length > 0;
  return Boolean(normalizeRich(remote?.bodyTop) || hasStructuredTable || normalizeRich(remote?.bodyBottom));
}

function unwrap(entry) {
  if (!entry || typeof entry !== 'object') return {};
  return entry.attributes && typeof entry.attributes === 'object' ? entry.attributes : entry;
}

function normalizeTableColumns(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => {
      const o = unwrap(x);
      return o.label != null ? String(o.label).trim() : '';
    })
    .filter(Boolean);
}

function normalizeTableRows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const o = unwrap(row);
      const title = o.title != null ? String(o.title).trim() : '';
      const cells = Array.isArray(o.cells)
        ? o.cells
            .map((c) => {
              const cell = unwrap(c);
              return cell.value != null ? String(cell.value).trim() : '';
            })
        : [];
      return { title, cells };
    })
    .filter((r) => r.title || r.cells.length > 0);
}

export default function AnnexeEnfantUniquePageShell({
  strapiApiId = 'annexe-enfant-unique',
  defaultSectionLabel = 'Annexes',
  defaultTitle = 'Enfant Unique',
  defaultTableSectionTitle = 'Explication pour remplir le formulaire',
  StaticFallback,
  fadeTriggerOnce = true,
}) {
  const [sectionLabel, setSectionLabel] = useState(defaultSectionLabel);
  const [title, setTitle] = useState(defaultTitle);
  const [tableSectionTitle, setTableSectionTitle] = useState(defaultTableSectionTitle);
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${title} | Tsahal Connection`;
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const attrs = await fetchStrapiSingleType(strapiApiId, { populate: 'populate=*' });
        if (cancelled) return;
        if (attrs) {
          setSectionLabel(pickCmsText(attrs.sectionLabel, defaultSectionLabel));
          setTitle(pickCmsTitle(attrs.title, defaultTitle));
          setTableSectionTitle(pickCmsText(attrs.tableSectionTitle, defaultTableSectionTitle));
          setRemote(attrs);
        } else {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setTableSectionTitle(defaultTableSectionTitle);
          setRemote(null);
        }
      } catch {
        if (!cancelled) {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setTableSectionTitle(defaultTableSectionTitle);
          setRemote(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [strapiApiId, defaultSectionLabel, defaultTitle, defaultTableSectionTitle]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '40vh', alignItems: 'center' }}>
          <CircularProgress sx={{ color: '#1398aa' }} />
        </Box>
        <Footer />
      </div>
    );
  }

  const bodyTop = normalizeRich(remote?.bodyTop);
  const bodyBottom = normalizeRich(remote?.bodyBottom);
  const tableColumns = normalizeTableColumns(remote?.tableColumns);
  const tableRows = normalizeTableRows(remote?.tableRows);
  const hasStructuredTable = tableColumns.length > 0 && tableRows.length > 0;
  const useCms = hasCms(remote);

  return (
    <div>
      <Navbar />
      <Fade triggerOnce={fadeTriggerOnce}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '800', fontSize: '1.2rem', marginBottom: '2rem', color: '#ae39ed', textAlign: 'center' }}>
            {sectionLabel}
          </h1>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '700', fontSize: '48px', textAlign: 'center' }}>{title}</h1>
          <img src={hr} alt="" width="80" height="50" />
        </div>
        {useCms ? (
          <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
            {bodyTop ? <StrapiRichText source={bodyTop} sx={richTextSx} /> : null}
            {hasStructuredTable ? (
              <>
                <h2 style={{ fontFamily: 'Montserrat', lineHeight: 1.8, fontSize: '34px', marginBottom: '1rem', marginTop: '1rem', fontWeight: 700 }}>
                  {tableSectionTitle}
                </h2>
                <TableContainer component={Paper} style={{ marginBottom: '4rem' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {tableColumns.map((label, idx) => (
                          <TableCell key={`th-${idx}`} style={{ backgroundColor: '#EBEAFF', fontWeight: 600, fontFamily: 'Montserrat' }}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map((row, rowIndex) => (
                        <TableRow key={`row-${rowIndex}`}>
                          <TableCell style={{ backgroundColor: '#EBEAFF', fontWeight: 600, fontFamily: 'Montserrat' }}>{row.title}</TableCell>
                          {tableColumns.slice(1).map((_, colIndex) => (
                            <TableCell key={`td-${rowIndex}-${colIndex}`} style={{ fontFamily: 'Montserrat' }}>
                              {row.cells[colIndex] ?? ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}
            {bodyBottom ? <StrapiRichText source={bodyBottom} sx={richTextSx} /> : null}
          </Container>
        ) : (
          <StaticFallback />
        )}
        <Footer />
      </Fade>
    </div>
  );
}
